import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Big } from 'foundations-components/transitional/components/Big';
import { CopyTextArea } from 'foundations-components/transitional/components/CopyTextArea';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { InputGroup } from 'foundations-components/transitional/components/InputGroup';
import { Link } from 'foundations-components/transitional/components/Link';
import { Select } from 'foundations-components/transitional/components/Select';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AuthStepItemType = {
  Password: 'Password',
  Text: 'Text',
  Select: 'Select',
  Email: 'Email',
  Url: 'Url',
  Html: 'Html',
  InfoBlock: 'InfoBlock',
  Snippet: 'Snippet'
};
export const UrlPartType = {
  Static: 'Static',
  Select: 'Select',
  Domain: 'Domain',
  Path: 'Path'
};
export const AuthStepType = {
  Step: 'Step'
};
const emailRegex = new RegExp('^.+@.+\\..{2,}$');

// Helper function to create help link from item-specific help
const createItemHelpLink = itemHelp => /*#__PURE__*/_jsx(Link, {
  external: true,
  href: itemHelp.link,
  children: itemHelp.text
});

// Helper function to create help link from generic help string
const createGenericHelpLink = helpUrl => /*#__PURE__*/_jsx(Link, {
  external: true,
  href: helpUrl,
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "universalAuth.auth.helpText"
  })
});

// Helper function to determine which help content to use, preferencing item help over generic help
const getHelpContent = (itemHelp, genericHelp) => {
  if (itemHelp) {
    return createItemHelpLink(itemHelp);
  }
  if (genericHelp) {
    return createGenericHelpLink(genericHelp);
  }
  return null;
};

// Helper function for TextStep that combines description with help content
const getTextStepHelpContent = (description, itemHelp, genericHelp) => {
  const helpLink = getHelpContent(itemHelp, genericHelp);
  if (description && helpLink) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Text, {
        children: description
      }), helpLink]
    });
  }
  if (description) {
    return /*#__PURE__*/_jsx(Text, {
      children: description
    });
  }
  return helpLink;
};
export function AuthStepItemView({
  item,
  help,
  label,
  params,
  onChange
}) {
  var _item$tooltip5;
  switch (item.type) {
    case AuthStepItemType.Password:
      {
        var _item$tooltip;
        const value = params[item.name];
        return /*#__PURE__*/_jsx(FormControl, {
          label: label,
          required: item.required,
          tooltip: (_item$tooltip = item.tooltip) === null || _item$tooltip === void 0 ? void 0 : _item$tooltip.text,
          help: getHelpContent(item.help, help),
          children: /*#__PURE__*/_jsx(TextInput, {
            type: "password",
            onChange: event => onChange(item.name, event.target.value),
            value: value
          })
        });
      }
    case AuthStepItemType.Text:
      {
        var _item$tooltip2;
        const value = params[item.name];
        return /*#__PURE__*/_jsx(FormControl, {
          label: label,
          required: item.required,
          tooltip: (_item$tooltip2 = item.tooltip) === null || _item$tooltip2 === void 0 ? void 0 : _item$tooltip2.text,
          help: getTextStepHelpContent(item.description, item.help, help),
          children: /*#__PURE__*/_jsx(TextInput, {
            type: "text",
            onChange: event => onChange(item.name, event.target.value),
            value: value,
            placeholder: item.placeholder ? item.placeholder : null
          })
        });
      }
    case AuthStepItemType.Email:
      {
        var _item$tooltip3;
        const value = params[item.name];
        const isInvalid = value ? !emailRegex.test(value) : false;
        return /*#__PURE__*/_jsx(FormControl, {
          label: label,
          required: item.required,
          tooltip: (_item$tooltip3 = item.tooltip) === null || _item$tooltip3 === void 0 ? void 0 : _item$tooltip3.text,
          help: getHelpContent(item.help, help),
          error: isInvalid,
          validationMessage: isInvalid ? /*#__PURE__*/_jsx(Text, {
            as: "small",
            use: "error",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.defaultValidationErrors.Email"
            })
          }) : null,
          children: /*#__PURE__*/_jsx(TextInput, {
            type: "email",
            onChange: event => onChange(item.name, event.target.value),
            value: value
          })
        });
      }
    case AuthStepItemType.Html:
      {
        var _item$tooltip4;
        return /*#__PURE__*/_jsx(FormControl, {
          label: label,
          tooltip: (_item$tooltip4 = item.tooltip) === null || _item$tooltip4 === void 0 ? void 0 : _item$tooltip4.text,
          help: getHelpContent(item.help, help),
          children: /*#__PURE__*/_jsx("div", {
            dangerouslySetInnerHTML: {
              __html: item.html
            }
          })
        });
      }
    case AuthStepItemType.InfoBlock:
      return /*#__PURE__*/_jsx(FormControl, {
        label: label,
        tooltip: (_item$tooltip5 = item.tooltip) === null || _item$tooltip5 === void 0 ? void 0 : _item$tooltip5.text,
        help: help ? createGenericHelpLink(help) : null,
        children: /*#__PURE__*/_jsxs(Text, {
          children: [item.message, item.link && /*#__PURE__*/_jsxs(_Fragment, {
            children: [' ', /*#__PURE__*/_jsx(Link, {
              external: true,
              href: item.link.destination,
              children: item.link.text
            })]
          })]
        })
      });
    case AuthStepItemType.Select:
      {
        var _item$tooltip6;
        const value = params[item.name];
        return /*#__PURE__*/_jsx(FormControl, {
          label: label,
          required: item.required,
          tooltip: (_item$tooltip6 = item.tooltip) === null || _item$tooltip6 === void 0 ? void 0 : _item$tooltip6.text,
          help: getHelpContent(item.help, help),
          children: /*#__PURE__*/_jsx(Select, {
            options: item.options.map(option => ({
              text: option.label,
              value: option.value
            })),
            value: value,
            onChange: event => onChange(item.name, event.target.value),
            menuWidth: "auto"
          })
        });
      }
    case AuthStepItemType.Snippet:
      {
        var _item$tooltip7;
        return /*#__PURE__*/_jsx(FormControl, {
          label: label,
          tooltip: (_item$tooltip7 = item.tooltip) === null || _item$tooltip7 === void 0 ? void 0 : _item$tooltip7.text,
          help: getHelpContent(item.help, help),
          children: /*#__PURE__*/_jsxs(View, {
            children: [item.description && /*#__PURE__*/_jsx(Text, {
              marginBlockEnd: "200",
              children: item.description
            }), /*#__PURE__*/_jsx(CopyTextArea, {
              use: "code",
              value: item.snippet,
              readOnly: true,
              textAreaProps: {
                resize: 'vertical',
                rows: Math.min(item.snippet.split('\n').length, 8)
              }
            })]
          })
        });
      }

    //  {type : 'Url', name: string, parts: array<urlPart>, required: bool}
    case AuthStepItemType.Url:
      {
        var _item$tooltip9;
        const name = item.name;
        const value = params[item.name];
        const urlStep = item;
        const urlHelpContent = getHelpContent(item.help, help);
        const renderedParts = urlStep.parts.map((part, index) => {
          var _item$tooltip8;
          const key = `${part.type}${index.toString()}`;
          const partValue = value ? value[index] : undefined;
          let child;
          let error = false;
          const validationMessages = [];
          switch (part.type) {
            case UrlPartType.Static:
              child = /*#__PURE__*/_jsx(Big, {
                children: part.value
              });
              break;
            case UrlPartType.Select:
              child = /*#__PURE__*/_jsx(Select, {
                options: part.options.map(option => ({
                  text: option,
                  value: option
                })),
                value: partValue,
                onChange: event => {
                  const nextValue = event.target.value;
                  const nextValues = value && Array.isArray(value) ? value.slice() : new Array(urlStep.parts.length).fill('');
                  nextValues[index] = nextValue;
                  onChange(name, nextValues);
                },
                menuWidth: "auto"
              });
              break;
            case UrlPartType.Domain:
            case UrlPartType.Path:
              {
                if (part.validation) {
                  const isInvalid = partValue && !new RegExp(part.validation.regex).test(partValue);
                  if (isInvalid) {
                    error = true;
                    validationMessages.push( /*#__PURE__*/_jsx(Text, {
                      as: "small",
                      use: "error",
                      children: part.validation.message
                    }, `${part.type}${index.toString()}`));
                  }
                }
                child = /*#__PURE__*/_jsx(TextInput, {
                  placeholder: part.placeholder,
                  value: partValue,
                  onChange: event => {
                    const nextValue = event.target.value;
                    const nextValues = value && Array.isArray(value) ? value.slice() : new Array(urlStep.parts.length).fill('');
                    nextValues[index] = nextValue;
                    onChange(name, nextValues);
                  }
                });
                break;
              }
            default:
              child = null;
          }
          if (index > 0 && child) {
            child = /*#__PURE__*/_jsxs(Flex, {
              alignItems: "baseline",
              gap: "100",
              children: [/*#__PURE__*/_jsx(View, {
                children: part.type === 'Path' ? '/' : '.'
              }), /*#__PURE__*/_jsx(View, {
                children: child
              })]
            });
          }
          return /*#__PURE__*/_jsx(FormControl, {
            tooltip: (_item$tooltip8 = item.tooltip) === null || _item$tooltip8 === void 0 ? void 0 : _item$tooltip8.text,
            error: error,
            validationMessage: validationMessages,
            "aria-label": part.type,
            children: child
          }, key);
        });
        return urlStep.parts.length === 1 ? /*#__PURE__*/_jsx(FormControl, {
          label: label,
          required: urlStep.required,
          tooltip: (_item$tooltip9 = item.tooltip) === null || _item$tooltip9 === void 0 ? void 0 : _item$tooltip9.text,
          help: urlHelpContent,
          children: renderedParts[0]
        }) : /*#__PURE__*/_jsx(InputGroup, {
          label: label,
          required: urlStep.required,
          use: "none",
          help: urlHelpContent,
          children: /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            gap: "100",
            children: renderedParts
          })
        });
      }
    default:
      return null;
  }
}

// computes the initial params state, i.e. preselects the first dropdown option etc.
export function getParamDefaultsFromSteps(steps) {
  const params = {};
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    for (let j = 0; j < step.items.length; j++) {
      const {
        item
      } = step.items[j];
      if ('name' in item) {
        switch (item.type) {
          case 'Url':
            {
              const values = new Array(item.parts.length);
              for (let k = 0; k < item.parts.length; k++) {
                const part = item.parts[k];
                switch (part.type) {
                  case 'Static':
                    {
                      values[k] = part.value;
                      continue;
                    }
                  case 'Select':
                    if (part.options.length > 0) {
                      values[k] = part.options[0];
                      continue;
                    }

                  // eslint-disable-next-line
                  default:
                    values[k] = '';
                }
              }
              params[item.name] = values;
              break;
            }
          case 'Select':
            if (item.options.length > 0) {
              params[item.name] = item.options[0].value;
              continue;
            }
            break;
          default:
            params[item.name] = '';
        }
      }
    }
  }
  return params;
}
export function validateAuthStep(step, params) {
  for (let i = 0; i < step.items.length; i++) {
    const {
      item
    } = step.items[i];
    switch (item.type) {
      case 'Password':
      case 'Text':
        if (item.required && !params[item.name]) {
          return false;
        }
        break;
      case 'Select':
        {
          if (item.required) {
            const value = params[item.name];
            if (
            // missing value
            !value ||
            // not a valid option
            item.options.findIndex(o => o.value === value) === -1) {
              return false;
            }
          }
          break;
        }
      case 'Email':
        if (item.required && !emailRegex.test(params[item.name])) {
          return false;
        }
        break;
      case 'Url':
        for (let j = 0; j < item.parts.length; j++) {
          const part = item.parts[j];
          const values = params[item.name] || [];
          switch (part.type) {
            case 'Select':
              {
                const partValue = values[j];
                if (item.required && (
                // missing value
                !partValue ||
                // invalid option
                !part.options.includes(partValue))) {
                  return false;
                }
                break;
              }
            case 'Domain':
            case 'Path':
              if (item.required && !values[j]) {
                return false;
              } else if (part.validation && !new RegExp(part.validation.regex).test(values[j])) {
                return false;
              }
              break;

            /* Static */
            default:
              continue;
          }
        }
        break;
      default:
        continue;
    }
  }
  return true;
}
export function buildApiPayload(sessionId, steps, params, oAuthParams, oAuthState, oAuthScope, mcp, appId) {
  const payload = Object.assign({
    sessionId,
    authParams: {},
    inputParams: {},
    upstreamParams: {},
    oAuthParams: undefined
  }, appId !== undefined && {
    appId
  });
  steps.forEach(step => {
    step.items.forEach(({
      item
    }) => {
      if (item.type === AuthStepItemType.Url) {
        // a full url needs to be constructed from the url parts
        const value = params[item.name];
        const urlValues = value && Array.isArray(value) ? value : new Array(item.parts.length).fill('');

        // fill the static parts
        item.parts.forEach((part, partIx) => {
          if (part.type === UrlPartType.Static) {
            urlValues[partIx] = part.value;
          }
        });
        const pathIndex = item.parts.findIndex(part => part.type === UrlPartType.Path);
        const domainParts = pathIndex === -1 ? urlValues : urlValues.slice(0, pathIndex);
        const pathParts = pathIndex === -1 ? [] : urlValues.slice(pathIndex);
        const url = `${domainParts.map(domainPart => domainPart.trim()).join('.').replace(/^(https?|ftp):\/\//, '') // strip http(s):// or ftp://
        .replace(/\/$/, '') // strip trailing /
        }${pathParts.length > 0 ? `/${pathParts.map(pathPart => pathPart.trim()).join('/')}` : ''}`;
        payload.inputParams[item.name] = url;
      } else if ('name' in item) {
        const value = params[item.name];
        const trimmedValue = value && !Array.isArray(value) ? value.trim() : '';
        payload.inputParams[item.name] = trimmedValue;
      }
    });
  });

  // OAuth 2.0 specific params
  if (oAuthParams && oAuthState) {
    oAuthParams.forEach((value, key) => {
      payload.upstreamParams[key] = value;
    });

    // Add the OAuth 2.0 specific `state` and `scope` from frontend to `oauthParams`
    // This is necessary in case they need to be handled differently from `upstreamParams` in the backend.
    payload.oAuthParams = Object.assign({
      state: oAuthState,
      scope: oAuthScope
    }, mcp !== null && mcp !== void 0 && mcp.clientId ? {
      clientId: mcp.clientId
    } : {}, mcp !== null && mcp !== void 0 && mcp.serverUrl ? {
      mcpServerUrl: mcp.serverUrl
    } : {});
  }

  // For backwards compatibility, we copy `inputParams` to the `authParams`.
  // DataSyncExtensions and UnifiedAuth-service currently expect input params (and upstream params) to be in the auth params.
  // These will be fixed in the future.
  payload.authParams = Object.assign({}, payload.inputParams);
  return payload;
}